// src/components/BuildingDesigner/utils.js
//
// POST the assembled floor JSON to the Node backend, then prompt the user
// to save the DXF + JSON files to their chosen folder (falls back to a
// standard download on browsers without showSaveFilePicker).

const BACKEND_URL = "https://building-api.saudidune.com/api/building/generate";

function b64ToBlob(b64, mime = "application/octet-stream") {
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

async function saveBlob(blob, suggestedName, description, mime) {
  if (window.showSaveFilePicker) {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName,
        types: [{
          description,
          accept: { [mime]: [`.${suggestedName.split(".").pop()}`] },
        }],
      });
      const w = await handle.createWritable();
      await w.write(blob);
      await w.close();
      return true;
    } catch (err) {
      if (err.name === "AbortError") return false;
      console.warn("showSaveFilePicker failed, falling back:", err);
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = suggestedName;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return true;
}

export async function saveFloorAndDownload(floor) {
  const res = await fetch(BACKEND_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(floor),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Backend error ${res.status}: ${text}`);
  }
  const data = await res.json();
  const filename = data.filename || floor.name || "MyBuilding";

  const dxfBlob = b64ToBlob(data.dxf_base64, "image/vnd.dxf");
  const savedDxf = await saveBlob(dxfBlob,
    `${filename}.dxf`, "DXF drawing", "image/vnd.dxf");
  if (!savedDxf) return;

  const jsonBlob = new Blob([data.json_text], { type: "application/json" });
  await saveBlob(jsonBlob,
    `${filename}.json`, "Project JSON", "application/json");
}

// ── Open a saved project JSON ────────────────────────────────────────────
// Returns the parsed floor object, or null if the user cancelled.
// Uses the modern showOpenFilePicker when available, otherwise falls back
// to a hidden <input type="file">.
export async function openProjectJson() {
  const readAsText = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload  = () => resolve(r.result);
    r.onerror = () => reject(new Error("File read failed"));
    r.readAsText(file);
  });

  let text = null;
  if (window.showOpenFilePicker) {
    try {
      const [handle] = await window.showOpenFilePicker({
        types: [{
          description: "Project JSON",
          accept: { "application/json": [".json"] },
        }],
        multiple: false,
      });
      const file = await handle.getFile();
      text = await readAsText(file);
    } catch (err) {
      if (err.name === "AbortError") return null;
      // fall through to legacy path
    }
  }
  if (text == null) {
    // Legacy fallback: hidden input.
    text = await new Promise((resolve) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".json,application/json";
      input.onchange = async () => {
        const f = input.files?.[0];
        if (!f) resolve(null);
        else    resolve(await readAsText(f));
      };
      input.click();
    });
    if (!text) return null;
  }

  let data;
  try { data = JSON.parse(text); }
  catch (e) { throw new Error("Selected file isn't valid JSON."); }

  // Sanitize customs — earlier builds of the frontend accidentally saved
  // extra fields (type, center_x, center_y) that Python doesn't accept
  // on CustomRoom.__init__. Strip them so old files still load and save.
  const cleanCustoms = (Array.isArray(data.customs) ? data.customs : [])
    .map(c => ({
      name:  c.name,
      label: c.label || c.name,
      points: Array.isArray(c.points) ? c.points : [],
      closed: c.closed !== false,   // default true
      doors:      Array.isArray(c.doors)      ? c.doors      : [],
      windows:    Array.isArray(c.windows)    ? c.windows    : [],
      open_doors: Array.isArray(c.open_doors) ? c.open_doors : [],
    }));

  // Sanitize texts (Stage 3x.5) — imported from DXF reader with the
  // text layer role, or produced by future room-reconstruction. Each
  // entry: {x, y, content}. Filter out garbage so downstream code that
  // reads t.x / t.y / t.content is safe.
  const cleanTexts = (Array.isArray(data.texts) ? data.texts : [])
    .map(t => ({
      x:       Number(t.x) || 0,
      y:       Number(t.y) || 0,
      content: String(t.content ?? ""),
    }))
    .filter(t => t.content.length > 0);

  // Normalise: ensure all the array fields exist so downstream code
  // (which does .map / .filter) doesn't crash on undefined.
  return {
    name:    data.name || "MyBuilding",
    rooms:   Array.isArray(data.rooms)   ? data.rooms   : [],
    stairs:  Array.isArray(data.stairs)  ? data.stairs  : [],
    shafts:  Array.isArray(data.shafts)  ? data.shafts  : [],
    customs: cleanCustoms,
    beams:   Array.isArray(data.beams)   ? data.beams   : [],
    columns: Array.isArray(data.columns) ? data.columns : [],
    slabs:   Array.isArray(data.slabs)   ? data.slabs   : [],
    texts:   cleanTexts,
  };
}
